<template>
  <div class="refund-options grid-x large-padding-top-m large-padding-bottom-m padding-top-s padding-bottom-s border-top">
    <div class="small-7 medium-3 medium-padding-right-xxxl">
      <h3 id="refund-options-heading" ref="refundOptionsHeadingRef" tabindex="0">Your Options</h3>
    </div>
    <div v-if="returnItemStore.returnItemsCurrentStep >= 2" class="small-5 medium-2 align-top padding-top-xxs text-right medium-text-left">
      <button
        v-if="!returnItemStore.returnItemsEditable.refundOptions && resolutionChoice"
        id="change-refund-option"
        class="link-body"
        @click="onChange"
      >
        Change
      </button>
    </div>
    <div v-if="returnItemStore.returnItemsCurrentStep >= 2" class="small-12 medium-7 small-margin-top-s medium-margin-top-z large-padding-left-m">
      <Notification v-if="notification" :severity="notification.type" :closable="notification.closable" class="margin-bottom-s">
        {{ notification.text }}
      </Notification>
      <template v-if="returnItemStore.returnItemsEditable.refundOptions">
        <CardRadio
          v-if="resolutionOptions"
          v-slot="{ option }"
          :options="resolutionOptions"
          :model-value="resolutionChoice"
          content-class="grid-x align-middle"
          @update:model-value="(selected, event) => handleResolutionChoiceInput(selected, event)"
        >
          <div class="cell auto">
            <span v-if="option.badge" class="badge small text-case" v-text="option.badge.value.toLowerCase()"></span>
            <p class="large" v-text="option.label.value"></p>
            <p v-if="option.description" class="cell grid-x small align-middle gray-1-color">
              {{ option.description.value }}
              <button
                v-if="option.description.tooltip"
                v-tooltip.bottom="{
                  value: option.description.tooltip.text,
                }"
                type="button"
                class="margin-left-xxs"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path :d="csgToolTip.d" fill="#626369" />
                </svg>
              </button>
            </p>
          </div>
        </CardRadio>
        <CardRadio
          v-else
          v-slot="{ option }"
          :options="refundOptions"
          value-key="choice"
          :model-value="resolutionChoice"
          content-class="grid-x align-middle"
          @update:model-value="(selected, event) => handleResolutionChoiceInput(selected, event)"
        >
          <div class="cell auto">
            <span :class="option.class || 'large'" v-text="option.label"></span>
            <p v-if="option.subText" class="cell small gray-1-color" v-text="option.subText"></p>
          </div>
          <button
            v-if="option.choice === config.REFUND_METHOD.EXCHANGE && resolutionChoice === option.choice"
            class="link-body"
            @click.stop="(event) => handleResolutionChoiceInput(config.REFUND_METHOD.EXCHANGE, event)"
          >
            Edit
          </button>
        </CardRadio>
        <div class="cell margin-bottom-s" aria-live="polite" v-text="selectionMessage"></div>
        <Form v-slot="{ meta }" @submit="onContinue">
          <template v-if="!UserStatus.isSignedIn()">
            <p class="margin-bottom-s">Enter your email to receive a return confirmation.</p>
            <InputText v-model="email" name="email" label="Email" rule="email" aria-required="true" />
          </template>
          <AppButton
            type="button"
            class="primary expanded continue"
            :class="{ 'margin-top-xs': !UserStatus.isSignedIn() }"
            :loading="loading"
            :disabled="isContinueDisabled(meta) || loading"
            @click="onContinue"
          >
            {{ change ? 'Save' : 'Continue' }}
          </AppButton>
        </Form>
      </template>

      <template v-else>
        <ProductCard
          v-if="primarySSREItem"
          ref="selectedSsreItemRef"
          class="padding-xs"
          show-border
          :product="primarySSREItem"
          :transformer="transformProductFromPrepareDeclaration"
          :image-width="84"
          :url-template="returnItemStore.prepareDeclarationResponse.urlTemplate"
          tabindex="-1"
        >
          <p class="padding-top-xs" tabindex="0">Option selected: {{ config.REFUND_METHOD_TEXT[resolutionChoice] }}</p>
        </ProductCard>
        <!--Preview-->
        <div v-else class="section-border" role="status" aria-live="polite">
          <p v-if="resolutionChoice" id="selected-refund-choice-text" class="medium" v-text="previewLabel"></p>
          <p v-if="previewDescription" id="selected-refund-choice-subtext" class="gray-1-color" v-text="previewDescription"></p>
        </div>
      </template>
    </div>
    <AccountVerificationSlideOut id="email-otp-slideout" :email="email" @continue="onContinueOTP" @o-t-p-abandoned="handleOtpAbandoned" />
  </div>
</template>

<script setup>
// Imports
import { computed, ref, watch, markRaw, defineAsyncComponent, nextTick } from 'vue';
import { UserStatus } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import Form from '@nuxt-common/atomic-ui/app/components/form';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification.vue';
import InputText from '@nuxt-common/atomic-ui/app/components/inputText/InputText.vue';
import { csgToolTip } from '@csgSvgIcons/icons';
// Local components
import CardRadio from '@/components/CardRadio.vue';
import AppButton from '@/components/AppButton.vue';
import ProductCard from '@/components/ProductCard.vue';

// Utility functions and constants
import { transformProductFromPrepareDeclaration } from '@/utils/returns/TransformProducts';
import { formatDate } from '@/utils/General';
import { getCardIcon, MULTIPLE_TENDERS } from '#shared/constants';

// Config
import * as config from 'config';

// Pinia Store & Composables
import { useReturnItemStore } from '@/stores/returns/useReturnItemStore';

const AccountVerificationSlideOut = defineAsyncComponent(
  () => import('@nuxt-common/account-verification-component/app/components/AccountVerificationSlideOut'),
);

const returnItemStore = useReturnItemStore();
const { registerSlideout, toggleSlideout } = useSlideout();

// Local State
const loading = ref(false);
const change = ref(false);
const email = ref('');
const notification = ref(null);
const resolutionChoice = ref('');
const selectedSsreItemRef = ref(null);
const refundOptionsHeadingRef = ref(null);

// Computed Properties
const selectedExchangeItem = computed(() => returnItemStore.exchangeItem.selected);
const accountType = computed(() => returnItemStore.returnItems?.refundMethodDetails?.accountType || '');
const accountNumber = computed(() => {
  const accountNumber = returnItemStore.returnItems?.refundMethodDetails?.accountNumber || '';

  return accountType.value.toLowerCase() === 'paypal' ? accountNumber : accountNumber.replace(/\*/g, '');
});
const isEGiftCardEnabled = computed(() => returnItemStore.returnItems?.eGiftCardEnabled && returnItemStore.IS_MARKETPLACE_ORDER);
const selfServiceEnabled = computed(() => returnItemStore.IS_REPLACE_ELIGIBLE || returnItemStore.IS_EXCHANGE_ELIGIBLE);
const isSelfServiceKSEnabled = computed(() => returnItemStore.returnItems?.isReplacementEnabled || returnItemStore.returnItems?.isExchangeEnabled);
const isEmailVerified = computed(() => returnItemStore.IS_ACCOUNT_VERIFIED);
const isStoreCreditUnverified = computed(
  () =>
    resolutionOptions.value &&
    resolutionChoice.value &&
    resolutionChoice.value.code === config.REFUND_METHOD.WALLET &&
    UserStatus.isSignedIn() &&
    !isEmailVerified.value,
);

const refundOptionSubText = computed(() => {
  switch (accountType.value.toLowerCase()) {
    case 'paypal':
      return `${config.REFUND_OPTIONS_SUB_TEXT.paypal} ${accountNumber.value}`;
    case 'klarna':
      return config.REFUND_OPTIONS_SUB_TEXT.Klarna;
    default:
      return '';
  }
});

const selectionMessage = computed(() => {
  if (resolutionOptions.value && resolutionChoice.value) {
    const option = resolutionOptions.value.find((option) => option.code === resolutionChoice.value.code);
    return option.selectionMessage?.value || `${resolutionChoice.value} is selected!`;
  }
  if (returnItemStore.returnItems?.mixedTenders && resolutionChoice.value === config.REFUND_METHOD.ORIGINAL)
    return config.refundOptionsText.originalPayment;
  if (accountType.value.toLowerCase() === 'star money' && resolutionChoice.value === config.REFUND_METHOD.ORIGINAL)
    return config.refundOptionsText.starMoneyAndOriginal;
  if (accountType.value.toLowerCase() === 'gift card' && resolutionChoice.value === config.REFUND_METHOD.ORIGINAL)
    return config.refundOptionsText.giftCardAndOriginal;
  if (resolutionChoice.value === config.REFUND_METHOD.GIFT) return config.refundOptionsText.giftCard;
  if (resolutionChoice.value === config.REFUND_METHOD.EGIFT) return config.refundOptionsText.eGiftCard;

  return !resolutionChoice.value || [config.REFUND_METHOD.EXCHANGE, config.REFUND_METHOD.REPLACEMENT].includes(resolutionChoice.value)
    ? ''
    : config.refundOptionsText.default;
});

const replaceOption = () => ({
  choice: config.REFUND_METHOD.REPLACEMENT,
  label: config.REFUND_METHOD_TEXT.REPLACEMENT,
  message: config.refundOptionsText.replace(formatDate(returnItemStore.prepareDeclarationResponse.dropOffByDate)),
});

const exchangeOption = () => ({
  choice: config.REFUND_METHOD.EXCHANGE,
  label: config.REFUND_METHOD_TEXT.EXCHANGE,
  message: config.refundOptionsText.exchange(formatDate(returnItemStore.prepareDeclarationResponse.dropOffByDate)),
});

const originalOption = () => {
  const optionObject = {
    choice: config.REFUND_METHOD.ORIGINAL,
    label: '',
  };
  if (returnItemStore.returnItems?.mixedTenders || ['star money', 'gift card'].includes(accountType.value.toLowerCase()) || !accountType.value) {
    optionObject.label = 'Original Payment Method';
  } else if (['klarna', 'paypal'].includes(accountType.value.toLowerCase())) {
    optionObject.class = getCardIcon(accountType.value).class;
    optionObject.subText = refundOptionSubText.value;
  } else optionObject.label = `${accountType.value} ending in ${accountNumber.value}`;

  return optionObject;
};

const giftCardOption = () => ({
  choice: isEGiftCardEnabled.value ? config.REFUND_METHOD.EGIFT : config.REFUND_METHOD.GIFT,
  label: isEGiftCardEnabled.value ? `${config.brandName} E-Gift Card` : `${config.brandName} Gift Card`,
});

const eGiftCardOption = () => ({
  choice: config.REFUND_METHOD.EGIFT,
  label: `${config.brandName} E-Gift Card`,
});

const applySelfServiceLabels = (original_option, gift_option, e_gift_option) => {
  const refundToPrefix = 'Refund to ';
  if (!['klarna', 'paypal'].includes(accountType.value.toLowerCase())) {
    original_option.label = refundToPrefix + original_option.label;
  }
  gift_option.label = refundToPrefix + gift_option.label;
  e_gift_option.label = refundToPrefix + e_gift_option.label;
};

const addRefundOptions = (options, original_option, gift_option, e_gift_option, replace_option, exchange_option) => {
  if (returnItemStore.IS_REPLACE_ELIGIBLE) options.push(replace_option);
  else if (returnItemStore.IS_EXCHANGE_ELIGIBLE) options.push(exchange_option);

  if (!returnItemStore.returnItems?.returnEligiblePaymentMethodsEnabled) {
    options.push(original_option);
    if (returnItemStore.returnItems?.showRefundMethodStatus) options.push(gift_option);
  } else {
    const optionsFromAPI = returnItemStore.returnItems?.returnEligiblePaymentMethods ?? [];
    if (optionsFromAPI.includes(config.REFUND_METHOD.ORIGINAL)) options.push(original_option);
    if (optionsFromAPI.includes(config.REFUND_METHOD.GIFT)) options.push(gift_option);
    if (optionsFromAPI.includes(config.REFUND_METHOD.EGIFT)) options.push(e_gift_option);
  }
};
// TODO: Once prepare resolution options changes are 100% we can remove this refundOptions
const refundOptions = computed(() => {
  if (!returnItemStore.returnItems || !Object.keys(returnItemStore.returnItems).length) return [];
  const options = [];
  // Replace Option
  const replace = replaceOption();
  // Exchange Option
  const exchange = exchangeOption();
  // Original Payment
  const original = originalOption();
  // Gift Card
  const gift = giftCardOption();
  // E-Gift Card
  const egift = eGiftCardOption();

  /**
   * Once selfService is 100%, we can update config refund method text and prefix with refund to and clean up this.
   */
  if (selfServiceEnabled.value) {
    applySelfServiceLabels(original, gift, egift);
  }
  addRefundOptions(options, original, gift, egift, replace, exchange);

  return options;
});

/**
 * reading resolution options from prepare declaration
 */
const resolutionOptions = computed(() => returnItemStore.prepareDeclarationResponse.resolutionOptions);

// TODO: once prepare resolution options changes are 100% we will rename all refundChoice instances to resolutionChoice
const previewLabel = computed(() => {
  if (!resolutionChoice.value) return '';
  if (resolutionOptions.value) {
    return resolutionChoice.value.previewLabel.value;
  }
  if ([config.REFUND_METHOD.REPLACEMENT, config.REFUND_METHOD.EXCHANGE].includes(resolutionChoice.value))
    return config.REFUND_METHOD_TEXT[resolutionChoice.value];
  return `Refund To ${config.REFUND_METHOD_TEXT[resolutionChoice.value]}`;
});
// TODO: once prepare resolution options changes are 100% we will rename all refundChoice instances to resolutionChoice
const previewDescription = computed(() => {
  if (resolutionChoice.value && resolutionOptions.value) {
    return resolutionChoice.value.previewDescription.value;
  }
  if (resolutionChoice.value !== config.REFUND_METHOD.ORIGINAL) return '';
  if (returnItemStore.returnItems?.tenders.length > 1) return MULTIPLE_TENDERS;
  if (accountType.value.toLowerCase() === 'star money') return accountType.value;
  if (accountType.value.toLowerCase() === 'paypal') return `${accountType.value} associated with ${accountNumber.value}`;
  if (accountType.value && accountNumber.value) return `${accountType.value} ending in ${accountNumber.value}`;
  return '';
});

const primarySSREItem = computed(() => {
  const isReplaceOrExchange = [config.REFUND_METHOD.REPLACEMENT, config.REFUND_METHOD.EXCHANGE].includes(resolutionChoice.value);
  const prepareCartonLine = returnItemStore.prepareDeclarationResponse.cartonLines?.[0];
  if (selectedExchangeItem.value.upcNumber) prepareCartonLine.upc = selectedExchangeItem.value.upcNumber;
  return isReplaceOrExchange && prepareCartonLine ? prepareCartonLine : false;
});

// Watchers
watch(
  () => returnItemStore.returnItemsEditable.refundOptions,
  async (sectionOpened) => {
    const sectionClosed = !sectionOpened;
    const { refundOption } = returnItemStore.returnSelections;
    if (sectionOpened && !refundOption) {
      handleSectionOpened();
      await nextTick();
      if (refundOptionsHeadingRef.value) {
        refundOptionsHeadingRef.value.focus();
      }
    } else if (sectionClosed && !refundOption) {
      /*
       * when section is closed and no refund option is selected (meaning user opened the section but didn't select any option and closed it)
       * this case is only possible if user moved back to return items section.
       * we reset the refund choice and email,
       */
      resolutionChoice.value = '';
      email.value = '';
    } else if (sectionClosed && refundOption) {
      /*
       * when section is closed and refund option is selected (meaning user opened the section,
       * selected an option or is good with pre selected option and then closed it)
       */

      /*
       * For SSR&E, we only show the UPS return method if the user selects Replacement or Exchange as the refund option.
       * If the user changes their refund selection to a different option,
       * we re-expand the return method section and display all available return methods.
       */
      const { returnMethod } = returnItemStore.returnSelections;

      if (returnMethod && selfServiceEnabled.value) {
        const returnEditable = {
          returnItems: false,
          refundOptions: false,
          shippingAddress: returnItemStore.SHOW_SHIPPING_ADDRESS_SECTION,
          returnMethod: !returnItemStore.SHOW_SHIPPING_ADDRESS_SECTION,
        };

        returnItemStore.SET_RETURN_EDITABLE(returnEditable);
        returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(3);

        if (returnItemStore.SHOW_SHIPPING_ADDRESS_SECTION) returnItemStore.SET_RETURN_SELECTIONS({ returnMethod: '' });
      }

      if (resolutionOptions.value) {
        resolutionChoice.value = resolutionOptions.value.find((option) => option.code === returnItemStore.returnSelections.refundOption.choice) || '';
      } else {
        resolutionChoice.value = returnItemStore.returnSelections.refundOption.choice;
      }
      email.value = returnItemStore.returnSelections.refundOption.email;
    }
  },
  { immediate: true },
);

watch(isEmailVerified, (isVerified) => {
  if (isVerified) {
    toggleSlideout('email-otp-slideout');
    onContinue();
  }
});

// Component Logic (Functions)
const handleSectionOpened = () => {
  /*
   * when section is opened and no refund option is selected (meaning it's the first time the section is opened)
   */

  // If prepare declaration has resolution option then we read the first object and set it as first option
  if (resolutionOptions.value) {
    resolutionChoice.value = resolutionOptions.value[0];
    fireTag('link', {
      event_name: 'refund method',
      site_action: 'refund method start',
      action_location: 'order details',
    });
    return;
  }

  // FALLBACK TO UI LOGIC TO SELECT FIRST REFUND CHOICE BY DEFAULT
  const firstOption = (refundOptions.value.length && refundOptions.value[0].choice) || '';
  /*
   * set default selected option.
   * If self service is enabled, we don't set any default option and force user to select one.
   * If self service is not enabled, we set the first option from the list as default
   */
  resolutionChoice.value = selfServiceEnabled.value ? '' : firstOption;

  // fire analytics tag when refund option section is opened.
  if (isSelfServiceKSEnabled.value) {
    const hasSsreOption = [config.REFUND_METHOD.REPLACEMENT, config.REFUND_METHOD.EXCHANGE].includes(firstOption);
    fireTag('link', {
      return_option: hasSsreOption ? firstOption.toLowerCase() : 'refund',
      event_name: 'select return option',
      return_order_id: returnItemStore.returnItems.orderNumber,
      site_action: 'return options start',
      action_location: 'order details',
    });
  } else {
    fireTag('link', {
      event_name: 'refund method',
      site_action: 'refund method start',
      action_location: 'order details',
    });
  }
};
const fireTagOnContinue = () => {
  if (isSelfServiceKSEnabled.value) {
    const ssreOptionSelected = [config.REFUND_METHOD.REPLACEMENT, config.REFUND_METHOD.EXCHANGE].includes(resolutionChoice.value);
    fireTag('link', {
      return_option: ssreOptionSelected ? resolutionChoice.value.toLowerCase() : 'refund',
      event_name: 'select return option',
      return_order_id: returnItemStore.returnItems.orderNumber,
      site_action: 'return options complete',
      action_location: 'order details',
    });
  } else {
    // TODO: Since the above KSs are enabled, we can remove this block during the cleanup phase.
    fireTag('link', {
      event_name: 'refund method',
      site_action: 'refund method complete',
      action_location: 'order details',
      refund_tender: resolutionChoice.value === config.REFUND_METHOD.ORIGINAL ? 'original payment method' : 'gift card',
    });
  }
};

const onContinueOTP = () => {
  returnItemStore.SET_ACCOUNT_VERIFIED(true);
  notification.value = null;
};

const handleOtpAbandoned = () => {
  notification.value = {
    type: 'error',
    closable: false,
    text: config.OTP_ABANDONED_ERROR,
  };
};
const onContinue = async () => {
  if (resolutionOptions.value && isStoreCreditUnverified.value) {
    toggleSlideout('email-otp-slideout');
    return;
  }
  notification.value = null;
  /**
   * TODO: once the prepare declaration resolution option changes are 100% then we will update store refundOption
   * to resolutionOption or resolutionChoice
   * */
  const choice = resolutionOptions.value ? resolutionChoice.value.code : resolutionChoice.value;
  returnItemStore.SET_RETURN_SELECTIONS({ refundOption: { choice, email: email.value } });

  fireTagOnContinue();
  if (!returnItemStore.returnSelections.returnMethod) {
    const returnEditable = {
      returnItems: false,
      refundOptions: false,
      shippingAddress: false,
      returnMethod: true,
    };
    if (returnItemStore.SHOW_SHIPPING_ADDRESS_SECTION) {
      returnEditable.shippingAddress = true;
      returnEditable.returnMethod = false;
    }
    returnItemStore.SET_RETURN_EDITABLE(returnEditable);
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(3);
  } else {
    returnItemStore.SET_RETURN_EDITABLE({ returnItems: false, refundOptions: false, returnMethod: false });
  }
};

const isContinueDisabled = (meta) => {
  if (!resolutionChoice.value) return true;
  if (!UserStatus.isSignedIn() && (!email.value || (email.value && !meta?.valid))) return true;
  return false;
};
const onChange = () => {
  change.value = true;
  if (returnItemStore.isReturnMethodIneligible && returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_METHOD_INELIGIBLE(false);
  }
  if (!returnItemStore.returnSelections.returnMethod) {
    returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(2);
  }
  returnItemStore.SET_RETURN_EDITABLE({ returnItems: false, refundOptions: true, returnMethod: false });
  fireTag('link', {
    link_name: 'change refund method',
    event_name: 'change refund method',
  });
};
const fireExchangeItemSelectedTag = () => {
  const exchangeEligibleItem = returnItemStore.prepareDeclarationResponse.cartonLines[0];
  const primaryItem = exchangeEligibleItem.upcSubstitutionMap[returnItemStore.exchangeItem.selected.upcNumber];

  fireTag('link', {
    event_name: 'exchange drawer',
    product_id: [primaryItem.productId.toString()],
    product_color: [exchangeEligibleItem.colorMap[primaryItem.colorId]?.color.toString()],
    product_size: [exchangeEligibleItem.sizeMap[primaryItem.sizeId]?.name.toString()],
    product_price: [exchangeEligibleItem.priceMap[primaryItem.priceId]?.value.toString()],
    site_action: 'select exchange item complete',
    action_location: 'order history',
  });
};
const handleExchangeSlideout = async () => {
  /**
   * user clicks on exchange option
   * drawers open
   */
  registerSlideout({
    id: 'exchange-item-selection-slideout',
    title: config.EXCHANGE_DRAWER.title,
    props: {},
    slideoutAction: {
      // Close slideout action
      onCloseSlideout: () => {
        selectedSsreItemRef.value?.$el.focus();
      },
    },
    footer: {
      title: config.EXCHANGE_DRAWER.footer_title,
      class: 'primary expanded',
      // disabled:
      onClick: () => {
        if (!returnItemStore.exchangeItem.draft.colorId || !returnItemStore.exchangeItem.draft.sizeId) return;
        returnItemStore.SET_EXCHANGE_ITEM();
        fireExchangeItemSelectedTag();
        toggleSlideout('exchange-item-selection-slideout');
        resolutionChoice.value = config.REFUND_METHOD.EXCHANGE;
        returnItemStore.SET_RETURN_SELECTIONS({ refundOption: { choice: config.REFUND_METHOD.EXCHANGE } });
        fireTagOnContinue();
        if (!returnItemStore.returnSelections.returnMethod) {
          returnItemStore.SET_RETURN_EDITABLE({
            returnItems: false,
            refundOptions: false,
            shippingAddress: true,
            returnMethod: false,
          });
          returnItemStore.SET_RETURN_ITEMS_CURRENT_STEP(3);
        } else {
          returnItemStore.SET_RETURN_EDITABLE({
            returnItems: false,
            refundOptions: false,
            returnMethod: false,
            shippingAddress: false,
          });
        }
        returnItemStore.SET_ERRORS({ v2SubmitReturnError: null });
      },
    },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "exchange-item" */ '@/components/returns/ExchangeItem.vue'))),
  });
  // toggle drawer
  toggleSlideout('exchange-item-selection-slideout');
};
const handleResolutionChoiceInput = (choice, event) => {
  if (choice === config.REFUND_METHOD.EXCHANGE) {
    event.preventDefault();
    handleExchangeSlideout();
  } else {
    resolutionChoice.value = choice;
  }
};
</script>
<style lang="scss" brand="mcom" scoped>
.text-case {
  text-transform: capitalize;
}
</style>
<style lang="scss" brand="bcom" scoped>
.text-case {
  text-transform: uppercase;
}
</style>
<style lang="scss" scoped>
// Fix icon alignment/sizes. Please don't remove as these classes are required and injected from constants.
.third-party-payment-paypal {
  display: block;
  background-size: rem-calc(75) rem-calc(33);
  background-position: left;
}
.third-party-payment-klarna {
  display: block;
}

.badge {
  color: #e01a2b;
  border: rem-calc(1) solid #e01a2b;
  padding: rem-calc(2) rem-calc(8);
  border-radius: rem-calc(5);
  margin-bottom: rem-calc(2);
  display: inline-block;
}
</style>
